/**
 * Container for the new RAM Portal adoption modal. Gates visibility on the
 * RAM_LIVE_VISIBLE feature flag and once-per-day localStorage dismissal.
 */
import React from "react";
import { isFeatureEnabled } from "../../utils/featureFlagUtils";
import {
  getNewCamerasPlatformModalDismissedKey,
  RAM_LIVE_VISIBLE_FEATURE_FLAG,
} from "./interfaces";
import { NewCamerasPlatformModalView } from "./views";

export interface NewCamerasPlatformModalContainerProps {
  featureFlags?: Record<string, boolean>;
  salesforceId: string;
  salesforceContactId: string;
}

/** Uses the user's local calendar day so once-per-day dismissal matches login expectations. */
export const getTodayKey = (): string => {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
};

const wasDismissedToday = (
  salesforceContactId: string,
  todayKey: string
): boolean =>
  localStorage.getItem(
    getNewCamerasPlatformModalDismissedKey(salesforceContactId)
  ) === todayKey;

export const NewCamerasPlatformModal: React.FC<NewCamerasPlatformModalContainerProps> = ({
  featureFlags,
  salesforceId,
  salesforceContactId,
}) => {
  const [todayKey, setTodayKey] = React.useState(getTodayKey);
  const [dismissedThisSession, setDismissedThisSession] = React.useState(false);

  const featureEnabled = isFeatureEnabled(
    featureFlags,
    RAM_LIVE_VISIBLE_FEATURE_FLAG
  );
  const normalizedContactId = salesforceContactId ?? "";

  React.useEffect(() => {
    const syncToday = () => {
      const nextTodayKey = getTodayKey();
      setTodayKey((currentTodayKey) =>
        currentTodayKey === nextTodayKey ? currentTodayKey : nextTodayKey
      );
    };

    window.addEventListener("focus", syncToday);
    document.addEventListener("visibilitychange", syncToday);

    return () => {
      window.removeEventListener("focus", syncToday);
      document.removeEventListener("visibilitychange", syncToday);
    };
  }, []);

  React.useEffect(() => {
    setDismissedThisSession(false);
  }, [todayKey, normalizedContactId]);

  const handleClose = () => {
    localStorage.setItem(
      getNewCamerasPlatformModalDismissedKey(normalizedContactId),
      todayKey
    );
    setDismissedThisSession(true);
  };

  if (!featureEnabled || !salesforceId) {
    return null;
  }

  const open =
    !dismissedThisSession && !wasDismissedToday(normalizedContactId, todayKey);

  return (
    <NewCamerasPlatformModalView
      open={open}
      salesforceId={salesforceId}
      salesforceContactId={normalizedContactId}
      onClose={handleClose}
    />
  );
};
